import React from "react";
import type { CountryOption } from "./CountryChoice";
import { COUNTRY_STATE_CITIES, COUNTRY_CITIES } from "../components/CityChoice"; 
// 👆 you can put your big city objects into a separate file (recommended).
// If you don’t have cityData.ts yet, you can temporarily paste the two objects
// directly into this file and remove the import above.

export type CitySelection = {
  countryISO: string;
  state?: string; // only used for US
  city: string;
};

export type CityChoiceProps = {
  labelCountry: string;
  labelState: string;
  labelCities: string;

  countryOptions: { code: string; name: string }[]; 

  selectedCountryISO: string;
  onCountryChange: (iso: string) => void;

  selectedState: string;
  onStateChange: (state: string) => void;

  selectedCities: CitySelection[];
  onToggleCity: (countryISO: string, city: string, state?: string) => void;

  labelStyle: React.CSSProperties;
  selectStyle: React.CSSProperties;
};

const CityChoice: React.FC<CityChoiceProps> = ({
  labelCountry,
  labelState,
  labelCities,

  countryOptions,
  selectedCountryISO,
  onCountryChange,

  selectedState,
  onStateChange,

  selectedCities,
  onToggleCity,

  labelStyle,
  selectStyle,
}) => {
  // --- Country dropdown is a normal <select> (same look as continents) ---
  const countryNameByISO = React.useMemo(() => {
    return new Map(countryOptions.map((c) => [c.code, c.name]));
  }, [countryOptions]);

  // --- For US: states are available; for others: state dropdown is disabled/hidden ---
  const hasStates = selectedCountryISO === "US" && COUNTRY_STATE_CITIES?.US;

  const stateOptions = React.useMemo(() => {
    if (!hasStates) return [];
    return Object.keys(COUNTRY_STATE_CITIES.US).sort((a, b) => a.localeCompare(b));
  }, [hasStates]);

  // --- Cities list depends on country + (optional) state ---
  const citiesForSelection = React.useMemo(() => {
    if (!selectedCountryISO) return [];

    // US with state
    if (selectedCountryISO === "US") {
      if (!selectedState) return [];
      const arr = COUNTRY_STATE_CITIES.US[selectedState] ?? [];
      return [...arr].sort((a, b) => a.localeCompare(b));
    }

    // Other countries
    const arr = COUNTRY_CITIES[selectedCountryISO] ?? [];
    return [...arr].sort((a, b) => a.localeCompare(b));
  }, [selectedCountryISO, selectedState]);

  // --- city multi-select dropdown (same style pattern as CountriesDropdown) ---
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const isUS = selectedCountryISO === "US";

  React.useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!open) return;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  React.useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
    }
  }, [open]);

  const cityOptions = React.useMemo(() => {
    if (!selectedCountryISO) return [];
  
    if (isUS) {
      if (!selectedState) return []; // no cities until a state is picked
      return (COUNTRY_STATE_CITIES.US?.[selectedState] ?? []).map((name) => ({
        code: `${selectedCountryISO}-${selectedState}-${name}`, // or any stable id
        name,
      }));
    }
  
    // non-US: just take the country-level city list
    return (COUNTRY_CITIES[selectedCountryISO] ?? []).map((name) => ({
      code: `${selectedCountryISO}-${name}`,
      name,
    }));
  }, [selectedCountryISO, selectedState]);
  
  const normalizedQuery = query.trim().toLowerCase();
  const filteredCities = React.useMemo(() => {
    const base =
      normalizedQuery.length === 0
        ? citiesForSelection
        : citiesForSelection.filter((c) => c.toLowerCase().startsWith(normalizedQuery));

    return base; // already sorted above
  }, [citiesForSelection, normalizedQuery]);

  const citySummary =
  selectedCities.length === 0
    ? "Select cities"
    : selectedCities.length <= 2
      ? selectedCities.map((x) => x.city).join(", ")
      : `${selectedCities.length} selected`;

  const selectedCountryLabel =
    selectedCountryISO ? (countryNameByISO.get(selectedCountryISO) ?? selectedCountryISO) : "";

  return (
    <div>
      {/* Country */}
     {/* Country */}
<div style={labelStyle}>
  {labelCountry}
  <select
    value={selectedCountryISO}
    onChange={(e) => onCountryChange(e.target.value)}
    style={selectStyle}
  >
    <option value="">Select country</option>
    {countryOptions.map((c) => (
      <option key={c.code} value={c.code}>
        {c.name}
      </option>
    ))}
  </select>
</div>

{/* ✅ ONLY show State when US is selected */}
{isUS && (
  <div style={{ ...labelStyle, marginTop: 10 }}>
    {labelState}
    <select
      value={selectedState}
      onChange={(e) => onStateChange(e.target.value)}
      style={selectStyle}
    >
      <option value="">Select state</option>
      {stateOptions.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  </div>
)}


      {/* Cities multi select */}
      <div style={labelStyle}>
        {labelCities}

        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            ...selectStyle,
            textAlign: "left",
            cursor: citiesForSelection.length ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            opacity:
              !selectedCountryISO ||
              (selectedCountryISO === "US" && !selectedState)
                ? 0.6
                : 1,
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={
            !selectedCountryISO ||
            (selectedCountryISO === "US" && !selectedState)
          }
          title={
            !selectedCountryISO
              ? "Select a country first"
              : selectedCountryISO === "US" && !selectedState
                ? "Select a state first"
                : ""
          }
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {citySummary}
          </span>
          <span style={{ opacity: 0.8 }}>▾</span>
        </button>

        {open && (
          <div
            ref={panelRef}
            role="listbox"
            aria-multiselectable="true"
            style={{
              marginTop: 0,
              backgroundColor: "#020617",
              borderRadius: 16,
              border: "1px solid rgba(148,163,184,0.8)",
              boxShadow: "0 18px 40px rgba(0,0,0,0.6)",
              padding: 6,
              width: "95%",
            }}
          >
            {/* search */}
            <div style={{ padding: 6 }}>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search cities in ${selectedCountryLabel}${selectedCountryISO === "US" && selectedState ? ` (${selectedState})` : ""}…`}
                style={{
                  width: "90%",
                  ...selectStyle,
                  borderRadius: 12,
                  padding: "10px 12px",
                  backgroundColor: "rgba(2,6,23,0.65)",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setOpen(false);
                }}
              />
            </div>

            {/* scrollable list */}
            <div style={{ maxHeight: 240, overflowY: "auto", padding: 6 }}>
              {filteredCities.length === 0 ? (
                <div style={{ padding: "10px 10px", color: "rgba(226,232,240,0.8)" }}>
                  No matches
                </div>
              ) : (
                filteredCities.map((city) => {
                  const checked = selectedCities.some(
                    (x) =>
                      x.countryISO === selectedCountryISO &&
                      x.city === city &&
                      (x.state ?? "") === (selectedCountryISO === "US" ? (selectedState ?? "") : "")
                  );
                  return (
                    <button
                      key={city}
                      type="button"
                      onClick={() =>
                        onToggleCity(
                          selectedCountryISO,
                          city,
                          selectedCountryISO === "US" ? selectedState : undefined
                        )}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        borderRadius: 12,
                        border: "none",
                        background: checked ? "rgba(250, 204, 21, 0.18)" : "transparent",
                        color: "#e5e7eb",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: "1px solid rgba(148,163,184,0.85)",
                          backgroundColor: checked ? "rgba(250, 204, 21, 0.95)" : "transparent",
                          display: "inline-block",
                        }}
                      />
                      <span>{city}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CityChoice;
